import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Feed filtering utilities for spam reduction
 * Implements client-side filtering similar to Ditto's approach
 */

// Known spam URL shorteners and suspicious domains
const SUSPICIOUS_DOMAINS = [
  'bit.ly', 'tinyurl.com', 't.co', 'goo.gl', 'ow.ly',
  'short.link', 'is.gd', 'buff.ly', 'adf.ly',
];

const SPAM_KEYWORDS = [
  'free bitcoin', 'double your', 'send 1 get', 'crypto giveaway',
  'claim your', 'airdrop', '100% guaranteed', 'no risk',
  'click here', 'limited time', 'act now', 'exclusive deal',
];

/**
 * Check if a URL is suspicious
 */
function isSuspiciousUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return SUSPICIOUS_DOMAINS.some(domain => parsed.hostname.includes(domain));
  } catch {
    return false;
  }
}

/**
 * Check if content contains spam keywords
 */
function containsSpamKeywords(content: string): boolean {
  const lower = content.toLowerCase();
  return SPAM_KEYWORDS.some(keyword => lower.includes(keyword));
}

/**
 * Check for excessive mentions (>10 unique npubs)
 */
function hasExcessiveMentions(content: string): boolean {
  const mentions = content.match(/nostr:npub1[a-z0-9]+/gi) || [];
  const uniqueMentions = new Set(mentions);
  return uniqueMentions.size > 10;
}

/**
 * Check for excessive hashtags (>10)
 */
function hasExcessiveHashtags(content: string): boolean {
  const hashtags = content.match(/#\w+/g) || [];
  return hashtags.length > 10;
}

/**
 * Check for repetitive content (same word >5 times)
 */
function hasRepetitiveContent(content: string): boolean {
  const words = content.toLowerCase().split(/\s+/).filter(w => w.length > 3);
  const freq: Record<string, number> = {};
  for (const word of words) {
    freq[word] = (freq[word] || 0) + 1;
    if (freq[word] > 5) return true;
  }
  return false;
}

/**
 * Check for suspicious URLs in content
 */
function hasSuspiciousUrls(content: string): boolean {
  const urls = content.match(/https?:\/\/[^\s]+/g) || [];
  return urls.some(isSuspiciousUrl);
}

/**
 * Main spam heuristic check
 * Returns true if the event is likely spam
 */
export function isSpamHeuristic(event: NostrEvent): boolean {
  const content = event.content;
  
  // Check spam keywords
  if (containsSpamKeywords(content)) return true;
  
  // Check excessive mentions
  if (hasExcessiveMentions(content)) return true;
  
  // Check excessive hashtags
  if (hasExcessiveHashtags(content)) return true;
  
  // Check repetitive content
  if (hasRepetitiveContent(content)) return true;
  
  // Check suspicious URLs
  if (hasSuspiciousUrls(content)) return true;
  
  return false;
}

/**
 * Get NIP-36 content warning from event tags
 * Returns the reason string if present, null otherwise
 */
export function getContentWarning(event: NostrEvent): string | null {
  const cwTag = event.tags.find(([name]) => name === 'content-warning');
  if (cwTag && cwTag[1]) {
    return cwTag[1]; // The reason/description
  }
  return null;
}

/**
 * Check if an event has a content warning (NIP-36)
 */
export function hasContentWarning(event: NostrEvent): boolean {
  return getContentWarning(event) !== null;
}

/**
 * Detect if content appears to be in a given language
 * Uses a simple heuristic based on Unicode script ranges
 */
export function detectLanguage(content: string): string {
  const text = content.trim();
  if (!text) return 'unknown';

  // Count characters from different scripts
  const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
  const cjkChars = (text.match(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/g) || []).length;
  const cyrillicChars = (text.match(/[\u0400-\u04FF]/g) || []).length;
  const arabicChars = (text.match(/[\u0600-\u06FF]/g) || []).length;
  const totalChars = text.replace(/\s/g, '').length;
  
  if (totalChars === 0) return 'unknown';

  const latinRatio = latinChars / totalChars;
  const cjkRatio = cjkChars / totalChars;
  const cyrillicRatio = cyrillicChars / totalChars;
  const arabicRatio = arabicChars / totalChars;

  if (cjkRatio > 0.3) return 'zh';
  if (cyrillicRatio > 0.3) return 'ru';
  if (arabicRatio > 0.3) return 'ar';
  if (latinRatio > 0.5) return 'en';
  
  return 'unknown';
}

/**
 * Get the preferred language from localStorage
 */
export function getPreferredLanguage(): string {
  return localStorage.getItem('preferred-language') || 'all';
}

/**
 * Filter events by language if a preference is set
 */
export function filterByLanguage(events: NostrEvent[], preferredLang: string): NostrEvent[] {
  if (!preferredLang || preferredLang === 'all') return events;
  
  return events.filter(event => {
    // Check NIP-50 language tag first if available
    const langTag = event.tags.find(([name]) => name === 'l');
    if (langTag && langTag[1]) {
      return langTag[1].toLowerCase() === preferredLang;
    }
    // Fall back to content detection
    const detectedLang = detectLanguage(event.content);
    return detectedLang === preferredLang;
  });
}

/**
 * Filter feed events based on multiple criteria
 */
export interface FeedFilters {
  mutedPubkeys: Set<string>;
  mutedWords: string[];
  contentWarningPolicy: 'blur' | 'show' | 'hide'; // NIP-36 policy
  preferredLanguage: string; // ISO language code or 'all'
  trustGraph?: {
    getDistance: (pubkey: string) => number;
    shouldShowInGlobalFeed: (pubkey: string) => { show: boolean; weight: number };
  };
  hideDistance3Plus: boolean; // Hide strangers by default
}

export function filterFeedEvents(
  events: NostrEvent[],
  filters: FeedFilters
): NostrEvent[] {
  return events.filter(event => {
    // 1. Filter muted users
    if (filters.mutedPubkeys.has(event.pubkey)) return false;
    
    // 2. Filter muted words
    const content = event.content.toLowerCase();
    if (filters.mutedWords.some(word => content.includes(word.toLowerCase()))) {
      return false;
    }
    
    // 3. Filter spam heuristics
    if (isSpamHeuristic(event)) return false;
    
    // 4. Filter by trust graph distance (if available)
    if (filters.trustGraph && filters.hideDistance3Plus) {
      const { show } = filters.trustGraph.shouldShowInGlobalFeed(event.pubkey);
      if (!show) return false;
    }
    
    return true;
  });
}

/**
 * Sort feed events by trust distance and engagement
 * Distance 1 first, then distance 2, then others
 */
export function sortFeedByTrust(
  events: NostrEvent[],
  getDistance: (pubkey: string) => number
): NostrEvent[] {
  return [...events].sort((a, b) => {
    const distA = getDistance(a.pubkey);
    const distB = getDistance(b.pubkey);
    
    // Distance 1 always comes first
    if (distA !== distB) return distA - distB;
    
    // Within same distance, sort by timestamp (newest first)
    return b.created_at - a.created_at;
  });
}